export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const formatPrice = (price) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP'
  }).format(price);
};

export const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

export const getAvailableTimeSlots = (date) => {
  const slots = [];
  const startHour = 9;
  const endHour = 18;
  
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  
  return slots;
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getPhoneDigits = (phone) => {
  return String(phone || '').replace(/\D/g, '');
};

export const isValidPhone = (phone) => {
  const digits = getPhoneDigits(phone);
  return /^(?:56)?[2-9]\d{8}$/.test(digits);
};

export const normalizeRole = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
);

export const isSuperAdminRole = (role) => normalizeRole(role) === 'super_admin';

export const isAdminRole = (role) => ['admin', 'super_admin'].includes(normalizeRole(role));

export const normalizeCategoryKey = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
);

export const formatCategoryLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Sin categoria';
  return raw
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const buildServiceCategories = (products = []) => {
  const byKey = new Map();

  products.forEach((product) => {
    const raw = product?.Categoria || product?.CategoriaTexto || product?.category || '';
    const key = normalizeCategoryKey(raw);
    if (key && !byKey.has(key)) {
      byKey.set(key, formatCategoryLabel(raw));
    }
  });

  return [
    { key: 'all', label: 'Todos los Servicios' },
    ...Array.from(byKey, ([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es')),
  ];
};

// Base fija para la API/servidor público donde viven las imágenes.
// Orden de prioridad:
// 1. Variable de entorno REACT_APP_API_BASE (en build de Amplify/CI)
// 2. localStorage['api_base_override'] (para pruebas locales sin reconstruir)
// 3. URL remota por defecto (incluso en localhost, para que las imágenes subidas
//    desde el admin local se guarden en el servidor de producción y se vean en Amplify)
//    Si necesitas apuntar a localhost:4000, usa:
//    localStorage.setItem('api_base_override', 'http://localhost:4000')
export const API_BASE = (() => {
  try {
    const envBase = process.env.REACT_APP_API_BASE;
    if (envBase && /^https?:\/\//i.test(envBase)) {
      return envBase.replace(/\/$/, '');
    }
  } catch {}

  try {
    const override = localStorage.getItem('api_base_override');
    if (override && /^https?:\/\//i.test(override)) {
      return override.replace(/\/$/, '');
    }
  } catch {}

  return 'https://mora.emmavilla.site';
})();

// Normaliza diferentes formatos guardados en DB a una URL de imagen accesible
// Reglas:
// - Si ya es absoluta (http/https), se retorna tal cual
// - Si es "/uploads/archivo.jpg" o "uploads/archivo.jpg" -> se antepone API_BASE
// - Si es solo el nombre del archivo -> se asume en /uploads del API_BASE
// - Si está vacío/undefined -> retorna placeholder por defecto
// SVG inline para evitar depender de un archivo físico en /public
const FALLBACK_SVG = `
<svg width="640" height="360" viewBox="0 0 640 360" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Imagen no disponible">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#f8e7ff"/>
      <stop offset="100%" stop-color="#e6d4ff"/>
    </linearGradient>
  </defs>
  <rect width="640" height="360" fill="url(#g)"/>
  <g fill="#b08cff">
    <circle cx="120" cy="100" r="6"/>
    <circle cx="520" cy="70" r="4"/>
    <circle cx="580" cy="300" r="5"/>
  </g>
  <g fill="none" stroke="#c9a7ff" stroke-width="2">
    <rect x="40" y="40" width="560" height="280" rx="18"/>
  </g>
  <g transform="translate(0,12)">
    <path d="M120 250 L220 180 L280 220 L360 160 L520 260" stroke="#d3bbff" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <g text-anchor="middle" fill="#6a44b1">
    <text x="320" y="180" style="font: 700 26px system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, 'Helvetica Neue', Arial">Mora.Artt</text>
    <text x="320" y="208" style="font: 500 16px system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, 'Helvetica Neue', Arial" fill="#7f5bd1">Imagen no disponible</text>
  </g>
</svg>`;
const FALLBACK_DATA_URI = 'data:image/svg+xml;utf8,' + encodeURIComponent(FALLBACK_SVG);

export function getImageUrl(raw) {
  // Preferimos un data URI para garantizar que siempre haya imagen
  const fallback = FALLBACK_DATA_URI;
  if (!raw || typeof raw !== 'string') return fallback;
  // Normaliza barras invertidas de Windows y quita doble slash innecesario
  let url = raw.trim().replace(/\\/g, '/');
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      const uploadIndex = parsed.pathname.indexOf('/uploads/');
      if (uploadIndex >= 0) {
        return `${API_BASE}${parsed.pathname.slice(uploadIndex)}`;
      }
    } catch {}
    return url;
  }
  if (url.startsWith('/uploads/')) return `${API_BASE}${url}`;
  if (url.startsWith('uploads/')) return `${API_BASE}/${url}`;
  // Si parece ser un nombre de archivo
  if (/^[A-Za-z0-9_.-]+\.(png|jpg|jpeg|gif|webp)$/i.test(url)) {
    return `${API_BASE}/uploads/${url}`;
  }
  return url || fallback;
}
